import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { brandColors } from '@config/brandTokens';
import type { PaymentGateway } from '@types/index';

export const PAYMENT_GATEWAY_OPTIONS: Array<{
  id: PaymentGateway;
  name: string;
  detail: string;
}> = [
  {
    id: 'stripe',
    name: 'Stripe',
    detail: 'Cards worldwide',
  },
  {
    id: 'razorpay',
    name: 'Razorpay',
    detail: 'India cards, UPI, and netbanking',
  },
];

interface PaymentGatewayChooserProps {
  value: PaymentGateway;
  suggested?: PaymentGateway;
  onChange: (next: PaymentGateway) => void;
  disabled?: boolean;
}

const PaymentGatewayChooser: React.FC<PaymentGatewayChooserProps> = ({
  value,
  suggested,
  onChange,
  disabled,
}) => {
  return (
    <Box>
      <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '0.95rem' }}>
        How you pay
      </Typography>
      <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.35, mb: 1.25 }}>
        Choose Stripe or Razorpay. Suggested is based on your location — both work.
      </Typography>
      <Box
        role="radiogroup"
        aria-label="Payment method"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1,
        }}
      >
        {PAYMENT_GATEWAY_OPTIONS.map((option) => {
          const selected = value === option.id;
          return (
            <Box
              key={option.id}
              role="radio"
              aria-checked={selected}
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => onChange(option.id)}
              sx={{
                textAlign: 'left',
                cursor: disabled ? 'default' : 'pointer',
                p: 1.5,
                borderRadius: 1.5,
                border: selected ? `2px solid ${brandColors.navy}` : '1px solid #e2e8f0',
                bgcolor: selected ? '#f8fafc' : brandColors.white,
                fontFamily: '"Sora", Arial, sans-serif',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': disabled
                  ? undefined
                  : { borderColor: selected ? brandColors.navy : '#94a3b8' },
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '0.95rem' }}>
                  {option.name}
                </Typography>
                {suggested === option.id && (
                  <Chip
                    size="small"
                    label="Suggested"
                    sx={{ height: 20, fontWeight: 800, bgcolor: brandColors.ice, color: brandColors.navy }}
                  />
                )}
              </Stack>
              <Typography variant="caption" sx={{ color: brandColors.slate, display: 'block', mt: 0.35 }}>
                {option.detail}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PaymentGatewayChooser;
